import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, ThemeContext } from 'styled-components';
import classNames from 'classnames';
import { useSpring, animated } from 'react-spring';
import useComponentSize from '@rehooks/component-size';
import OriginalCard from 'react-bootstrap/Card';
import { recordStatisticsEvent } from '../../../../services/statistics-service';
import Truncator from './truncator';

const active = css`
  box-shadow: 0px 2px 9px rgba(0, 0, 0, 0.25);
  border-color: ${props => props.theme.primaryColor};
`;

const Card = styled(OriginalCard)`
  display: flex;
  width: 100%;
  flex-direction: column;
  padding: 32px ${props => (props.theme.isMobile ? 16 : 24)}px;
  margin-bottom: 20px;
  background-color: #FFFFFF;
  border: 1px solid;
  border-color: #d7dfe7;
  box-sizing: border-box;
  box-shadow: 0px 2px 9px rgba(0, 0, 0, 0.08);
  border-radius: 5px;
  ${props => props.theme.isXXLarge && `
    max-width: 840px;
  `};

  &:not(.expanded) {
    cursor: pointer;
    transition: box-shadow .3s ease-in-out;
    transition: border-color .3s ease-in-out;
  }

  &.expanded { ${active} }

  /* hover state on touch interface has a lingering effect. avoid that */
  ${props => !props.theme.isMobile && `
    &:not(.expanded):hover { ${active} }
  `};
`;

const catHeight = 20;
const toggleHeight = 28;
const Toggle = styled.span`
  z-index: 20;
  align-self: flex-end;
  margin-bottom: -${toggleHeight - 0.5 * (toggleHeight - catHeight) - 1}px;
  width: ${toggleHeight}px;
  height: ${toggleHeight}px;
  display: flex;
  justify-content: center;
  align-items: center;
  font: ${props => props.theme.fontWeightRegular} ${props => (props.theme.isMobile ? 26 : 30)}px/100% ${props => props.theme.fontFamilyBody};
  color: ${props => props.theme.primaryColor};
  cursor: pointer;

  &:hover {
    color: #ae8e38; /* 5% darkened */
  }
`;

const Category = styled.span`
  font: ${props => props.theme.fontWeightHeavy} 14px/17px ${props => props.theme.fontFamilyBody};
  color: ${props => props.theme.secondaryColor};
  text-transform: uppercase;
  height: ${catHeight}px;
`;

const Question = styled.span`
  font: ${props => props.theme.fontWeightBold} 21px/25px ${props => props.theme.fontFamilyBody};
  color: ${props => props.theme.bodyColor};
  min-height: 24px;
  margin-top: ${props => (props.theme.isXLarge ? 8 : 16)}px;
`;

const Answer = styled.div`
  font: ${props => props.theme.fontWeightNormal} 18px/130% ${props => props.theme.fontFamilyBody};
  color: ${props => props.theme.bodyColor};
  margin-top: 8px;
`;

const cah = 48; // collapsed answer height
export default function QandaItem({
  fundId, category, question, questionId, answer, expanded, onToggle,
}) {
  const { isLarge } = React.useContext(ThemeContext);
  const offset = isLarge ? 120 : 50; // This is the offset for navbar and sticky header(in large).
  const [rested, setRested] = React.useState(true);
  const cardRef = React.useRef();
  const answerRef = React.useRef();
  const { height } = useComponentSize(answerRef);
  const summaryAnimation = useSpring({ opacity: expanded ? 0 : 1 });
  const answerAnimation = useSpring({
    height: expanded ? Math.max(cah, height + 10) : cah,
    opacity: expanded ? 1 : 0,
    onFrame: () => {
      if (!rested && expanded && cardRef.current) {
        const { y } = cardRef.current.getBoundingClientRect();
        y < offset && window.scrollBy(0, y - offset);
      }
    },
    onRest: () => {
      !rested && expanded && setRested(true);
    },
  });

  const toggle = from => e => {
    e && e.preventDefault();
    if (from === 'body' && !expanded) {
      recordStatisticsEvent({
        event: 'fund_profile_qanda_expanding',
        statisticable: 'fund',
        id: fundId,
        clicked_url: `/question/${questionId}`,
        page: window.location.pathname,
      });

      setRested(false);
      onToggle(true);
    } else if (from === 'icon' && expanded) {
      onToggle(false);
    }
  };

  const truncated = <Truncator summary={answer} />;

  const fullBodyStyles = { overflowY: 'hidden', whiteSpace: 'pre-wrap' };

  return (
    <Card ref={cardRef} className={classNames({ expanded })} onClick={toggle('body')}>
      <Toggle className="toggle-icon" onClick={toggle('icon')}>{expanded ? '\u2212' : '+'}</Toggle>
      <Category>{category}</Category>
      <Question>{question}</Question>
      <animated.div style={{ ...summaryAnimation, height: cah }}>
        <Answer>{truncated}</Answer>
      </animated.div>
      <animated.div style={{ ...answerAnimation, ...fullBodyStyles, marginTop: -1 * cah }}>
        <Answer ref={answerRef}>{answer}</Answer>
      </animated.div>
    </Card>
  );
}

QandaItem.propTypes = {
  fundId: PropTypes.number.isRequired,
  category: PropTypes.string.isRequired,
  question: PropTypes.string.isRequired,
  questionId: PropTypes.number.isRequired,
  answer: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};
